import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import {
  getPastPapersAPI,
  getPastPaperFilesAPI,
} from "../../../services/portal/admin/pastPaperService";
import StudyPackCard from "../../../components/portal/StudyPackCard";
import { RoutePaths } from "../../../utility/RoutePaths";
import BunnyVideo from "../../../components/BunneyVideo";
import Plyr from "plyr";
import "plyr/dist/plyr.css";

const extractYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
    /youtube\.com\/live\/([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

const PastPaperDetailsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pastPaperId = searchParams.get("pastPaperId");

  const [loading, setLoading] = useState(true);
  const [paper, setPaper] = useState(null);
  const [files, setFiles] = useState([]);

  const playerRef = useRef(null);
  const plyrInstance = useRef(null);

  const youtubeVideoId = paper ? extractYouTubeVideoId(paper.VideoLink) : null;

  // Initialize Plyr after the player div is rendered and videoId is known
  useEffect(() => {
    if (!youtubeVideoId || !playerRef.current) return;

    plyrInstance.current = new Plyr(playerRef.current, {
      title: "",
      controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "fullscreen",
      ],
      youtube: { noCookie: true, rel: 0, showinfo: 0 },
    });

    return () => {
      plyrInstance.current?.destroy();
      plyrInstance.current = null;
    };
  }, [youtubeVideoId]);

  useEffect(() => {
    if (pastPaperId) {
      loadData();
    }
  }, [pastPaperId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [papersRes, filesRes] = await Promise.all([
        getPastPapersAPI(),
        getPastPaperFilesAPI(pastPaperId),
      ]);

      if (papersRes && papersRes.IsSuccessful && papersRes.Data) {
        const found = papersRes.Data.find(
          (p) => String(p.Id) === String(pastPaperId),
        );
        if (found) {
          setPaper(found);
        } else {
          showErrorToastMsg("Past paper not found");
        }
      }

      if (filesRes && filesRes.IsSuccessful && Array.isArray(filesRes.Data)) {
        setFiles(filesRes.Data);
      }
    } catch (error) {
      console.error("Error loading past paper details:", error);
      showErrorToastMsg("Failed to load past paper");
    } finally {
      setLoading(false);
    }
  };

  if (!pastPaperId) {
    return (
      <DashboardLayout title="Past Paper Details" subtitle="">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Invalid past paper link.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Past Paper Details"
      subtitle={paper ? paper.Title : "Loading..."}
    >
      <div className="mb-4">
        <button
          type="button"
          onClick={() => navigate(RoutePaths.STUDENT_PAST_PAPERS_PAGE)}
          className="text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          ← Back to past papers
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      ) : !paper ? (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Past paper not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Paper Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-2xl font-bold text-gray-900">{paper.Title}</h2>
            {paper.Description && (
              <p className="text-gray-600 mt-2">{paper.Description}</p>
            )}
          </div>

          {/* Video */}
          {paper.VideoLink && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Video</h3>
              {youtubeVideoId ? (
                <>
                  <style>{`
                    .plyr__title { display: none !important; }
                    .plyr__video-embed::before {
                      content: '';
                      position: absolute;
                      top: 0;
                      left: 0;
                      width: 100%;
                      height: 15%;
                      z-index: 10;
                      pointer-events: all;
                      background: transparent;
                    }
                  `}</style>
                  <div
                    ref={playerRef}
                    data-plyr-provider="youtube"
                    data-plyr-embed-id={youtubeVideoId}
                  />
                </>
              ) : (
                <BunnyVideo videoUrl={paper.VideoLink} />
              )}
            </div>
          )}

          {/* Files */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Files</h3>
            {files.length === 0 ? (
              <p className="text-gray-500 text-sm">
                No files available for this past paper.
              </p>
            ) : (
              <div className="flex flex-wrap gap-4">
                {files.map((file) => (
                  <StudyPackCard key={file.Id} document={file} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default PastPaperDetailsPage;
